import { DefaultPreview } from "#features/conversations/components/conversation-source/previews/DefaultPreview.tsx";
import { FilePreview } from "#features/conversations/components/conversation-source/previews/FilePreview.tsx";
import { JiraIssuePreview } from "#features/conversations/components/conversation-source/previews/JiraIssuePreview.tsx";
import { OfficevibeRecognitionPreview } from "#features/conversations/components/conversation-source/previews/OfficevibeRecognitionPreview.tsx";
import { TablePreview } from "#features/conversations/components/conversation-source/previews/TablePreview.tsx";
import { WebPagePreview } from "#features/conversations/components/conversation-source/previews/WebPagePreview.tsx";
import { SourcePreviewTitle } from "#features/conversations/components/conversation-source/source-preview/SourcePreviewTitle.tsx";
import type { SourcePreviewProps } from "#features/conversations/components/conversation-source/source-preview/SourcePreviewProps.ts";
import { ErrorBoundary } from "react-error-boundary";

/**
 * A preview reads fields off a source, which is third-party data relayed by the API. A field the
 * renderer assumes is present but that a connector left empty throws during render, and a render
 * error escapes to the route's `errorElement`, replacing the whole page with the error screen — a
 * hover destroys the conversation. The boundary keeps that blast radius at this tooltip.
 */
export const SourcePreviewCard = ({ source }: SourcePreviewProps) => {
    return (
        // A boundary holds its fallback until it is reset, and a tooltip instance is reused across
        // sources, so key the reset on the source: one unrenderable source must not poison the
        // preview of every source shown after it.
        <ErrorBoundary fallback={<SourcePreviewCardFallback source={source} />} resetKeys={[source.id]}>
            <SourcePreviewCardContent source={source} />
        </ErrorBoundary>
    );
};

const SourcePreviewCardContent = ({ source }: SourcePreviewProps) => {
    const { sourceType } = source;

    switch (sourceType) {
        case "jira_issue":
            return <JiraIssuePreview source={source} />;
        case "workleap_officevibe_recognition":
            return <OfficevibeRecognitionPreview source={source} />;
        case "web_page":
            return <WebPagePreview source={source} />;
        case "sharepoint_file":
        case "workleap_files_library_file":
            return <FilePreview source={source} />;
        case "table":
            return <TablePreview source={source} />;
        default:
            return <DefaultPreview source={source} />;
    }
};

/** Reads nothing but the title, so it stays renderable for the sources the previews cannot render. */
const SourcePreviewCardFallback = ({ source }: SourcePreviewProps) => {
    return (
        <div className="wai-source-card">
            <SourcePreviewTitle text={source.title ?? ""} />
        </div>
    );
};
